import React from 'react';
import { Phone, Clock, FileText, AlertTriangle, CheckCircle, BookOpen } from 'lucide-react';
export function HoursOfServicePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2748">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2749">
        <div className="container mx-auto px-4" data-id="element-2750">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2751">
            Logbook Requirements
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2752">
            Hours of Service & Daily Log Regulations
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-b-2 border-yellow-500" data-id="element-2753">
        <div className="container mx-auto px-4 py-4" data-id="element-2754">
          <div className="max-w-4xl mx-auto" data-id="element-2755">
            <div className="flex items-start" data-id="element-2756">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2757" />
              <p className="text-sm text-gray-800" data-id="element-2758">
                <strong data-id="element-2759">Important:</strong> Due to frequent changes in federal
                and state regulations, the Michigan Center for Truck Safety
                cannot ensure the accuracy of the material contained beyond the
                date of publication. For current information, contact the Center
                at{' '}
                <a href="tel:8006824682" className="text-blue-600 hover:underline font-semibold" data-id="element-2760">
                  (800) 682-4682
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2761">
        <div className="max-w-4xl mx-auto" data-id="element-2762">
          {/* General Information */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2763">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2764">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-2765" />
              General Information
            </h2>

            <div className="space-y-4" data-id="element-2766">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2767">
                <p className="text-gray-800" data-id="element-2768">
                  The daily log is an{' '}
                  <strong data-id="element-2769">accurate and complete record</strong> of a driver's
                  activities for each <strong data-id="element-2770">24-hour period</strong>.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2771">
                <div className="flex items-start" data-id="element-2772">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2773" />
                  <p className="text-gray-700" data-id="element-2774">
                    Unless exempt (see Short-Haul Provision or 100 air-mile
                    exemption below), all drivers must record their duty status
                    using the daily logbook
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2775">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2776" />
                  <p className="text-gray-700" data-id="element-2777">
                    All entries in the logbook shall be{' '}
                    <strong data-id="element-2778">
                      accurate, legible and in the driver's own handwriting
                    </strong>
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2779">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2780" />
                  <p className="text-gray-700" data-id="element-2781">
                    Employers must retain the daily logs at their principal
                    place of business (previous <strong data-id="element-2782">six months</strong>)
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2783">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2784" />
                  <p className="text-gray-700" data-id="element-2785">
                    Employers must maintain supporting documents to verify the
                    logs are true and accurate
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Short-Haul Provision */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2786">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2787">
              <Clock className="mr-3 text-green-600" size={28} data-id="element-2788" />
              Short-Haul Provision (Exemption)
            </h2>

            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6" data-id="element-2789">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2790">Applies to:</h3>
              <p className="text-gray-700" data-id="element-2791">
                Drivers who operate CMVs which{' '}
                <strong data-id="element-2792">do not require a CDL</strong> and who operate within a{' '}
                <strong data-id="element-2793">150 air-mile radius</strong> of their normal work
                reporting location
              </p>
            </div>

            <div className="space-y-4" data-id="element-2794">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2795">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2796">Requirements:</h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2797">
                  <li className="flex items-start" data-id="element-2798">
                    <span className="mr-2" data-id="element-2799">•</span>
                    <span data-id="element-2800">
                      May drive a maximum of <strong data-id="element-2801">11 hours</strong> after
                      coming on duty, following{' '}
                      <strong data-id="element-2802">10 or more consecutive hours off duty</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2803">
                    <span className="mr-2" data-id="element-2804">•</span>
                    <span data-id="element-2805">
                      <strong data-id="element-2806">Are not required to keep the daily log</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2807">
                    <span className="mr-2" data-id="element-2808">•</span>
                    <span data-id="element-2809">
                      May not drive beyond the <strong data-id="element-2810">14th hour</strong> after
                      coming on duty five days a week OR after the{' '}
                      <strong data-id="element-2811">16th hour</strong> after coming on duty two days a
                      week
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 100 Air-Mile Exemption */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2812">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2813">
              <Clock className="mr-3 text-blue-600" size={28} data-id="element-2814" />
              100 Air-Mile Exemption
            </h2>

            <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-6 mb-6" data-id="element-2815">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2816">Applies to:</h3>
              <p className="text-gray-700" data-id="element-2817">
                <strong data-id="element-2818">CDL drivers</strong> who operate within a{' '}
                <strong data-id="element-2819">100 air-mile radius</strong> of their normal work
                reporting location
              </p>
            </div>

            <div className="space-y-4" data-id="element-2820">
              <div className="bg-green-50 rounded-lg p-6" data-id="element-2821">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2822">Requirements:</h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2823">
                  <li className="flex items-start" data-id="element-2824">
                    <span className="mr-2" data-id="element-2825">•</span>
                    <span data-id="element-2826">
                      May drive a maximum of <strong data-id="element-2827">11 hours</strong> after
                      coming on duty, following{' '}
                      <strong data-id="element-2828">10 or more consecutive hours off duty</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2829">
                    <span className="mr-2" data-id="element-2830">•</span>
                    <span data-id="element-2831">
                      <strong data-id="element-2832">Are not required to keep the daily log</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2833">
                    <span className="mr-2" data-id="element-2834">•</span>
                    <span data-id="element-2835">
                      May not drive beyond the <strong data-id="element-2836">12th hour</strong> after
                      coming on duty five days a week OR after the{' '}
                      <strong data-id="element-2837">16th hour</strong> after coming on duty one day a
                      week
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2838">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2839">
              Exemption Comparison
            </h2>

            <div className="overflow-x-auto" data-id="element-2840">
              <table className="w-full border-collapse" data-id="element-2841">
                <thead data-id="element-2842">
                  <tr className="bg-gray-200" data-id="element-2843">
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2844">
                      Criteria
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2845">
                      Short-Haul Provision
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2846">
                      100 Air-Mile Exemption
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2847">
                  <tr className="bg-white" data-id="element-2848">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2849">
                      Driver Type
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2850">
                      CMVs that do NOT require CDL
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2851">
                      CDL drivers
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-2852">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2853">
                      Radius
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2854">
                      150 air-miles
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2855">
                      100 air-miles
                    </td>
                  </tr>
                  <tr className="bg-white" data-id="element-2856">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2857">
                      Max Driving Hours
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2858">
                      11 hours (after 10 consecutive hours off)
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2859">
                      11 hours (after 10 consecutive hours off)
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-2860">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2861">
                      Daily Log Required
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2862">No</td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2863">No</td>
                  </tr>
                  <tr className="bg-white" data-id="element-2864">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2865">
                      Max On-Duty Time
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2866">
                      14th hour (5 days/week) OR 16th hour (2 days/week)
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2867">
                      12th hour (5 days/week) OR 16th hour (1 day/week)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Requirements for Exemptions */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2868">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2869">
              Additional Requirements for Both Exemptions
            </h2>

            <div className="space-y-4" data-id="element-2870">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2871">
                <p className="text-lg mb-2" data-id="element-2872">
                  Drivers using the Short-Haul Provision or the 100 air-mile
                  exemption must:
                </p>
                <ul className="space-y-2 text-gray-100" data-id="element-2873">
                  <li className="flex items-start" data-id="element-2874">
                    <CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2875" />
                    <span data-id="element-2876">
                      Comply with the <strong data-id="element-2877">60-/70-hour rule</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2878">
                    <CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2879" />
                    <span data-id="element-2880">
                      May use the <strong data-id="element-2881">34-hour restart</strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Employer Record Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2882">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2883">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2884" />
              Employer Record Requirements
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6" data-id="element-2885">
              <p className="text-gray-800 mb-4" data-id="element-2886">
                Employers must maintain{' '}
                <strong data-id="element-2887">accurate and true records</strong> (timecards,
                timesheets, etc.) for each driver using the Short-Haul Provision
                or the 100 air-mile exemption.
              </p>
              <p className="text-gray-800" data-id="element-2888">
                The employer must always have the{' '}
                <strong data-id="element-2889">previous six month's records</strong> on file and
                available for review.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6" data-id="element-2890">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2891">
                These records must identify:
              </h3>

              <div className="space-y-3" data-id="element-2892">
                <div className="bg-white rounded-lg p-4" data-id="element-2893">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2894">The driver</p>
                  <p className="text-sm text-gray-600" data-id="element-2895">
                    Full identification of the driver
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2896">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2897">
                    The time the driver reports for duty
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2898">
                    Exact time driver begins work
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2899">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2900">
                    The time the driver is released from duty
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2901">
                    Exact time driver ends work
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2902">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2903">
                    The total on-duty hours each day
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2904">
                    Complete accounting of daily on-duty time
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2905">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2906">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-2907">
              <div className="flex items-start" data-id="element-2908">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2909" />
                <span data-id="element-2910">
                  Daily logs required for all drivers unless exempt under
                  Short-Haul or 100 air-mile provisions
                </span>
              </div>
              <div className="flex items-start" data-id="element-2911">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2912" />
                <span data-id="element-2913">
                  Logbook entries must be accurate, legible, and in driver's own
                  handwriting
                </span>
              </div>
              <div className="flex items-start" data-id="element-2914">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2915" />
                <span data-id="element-2916">
                  Employers must retain logs for 6 months at principal place of
                  business
                </span>
              </div>
              <div className="flex items-start" data-id="element-2917">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2918" />
                <span data-id="element-2919">
                  Short-Haul: Non-CDL drivers, 150 air-miles, 14th/16th hour
                  limit
                </span>
              </div>
              <div className="flex items-start" data-id="element-2920">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2921" />
                <span data-id="element-2922">
                  100 Air-Mile: CDL drivers, 100 air-miles, 12th/16th hour limit
                </span>
              </div>
              <div className="flex items-start" data-id="element-2923">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2924" />
                <span data-id="element-2925">
                  Both exemptions: 11-hour max driving, 10 consecutive hours off
                  required
                </span>
              </div>
              <div className="flex items-start" data-id="element-2926">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2927" />
                <span data-id="element-2928">
                  Employers must maintain timecards/timesheets for exempt
                  drivers (6 months)
                </span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2929">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2930">
              Need Help with Hours of Service Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-2931">
              Expert guidance on logbook requirements and hours of service
              regulations
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2932">
              <a href="tel:2484788382" className="inline-flex items-center justify-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2933">
                <Phone className="mr-3" size={24} data-id="element-2934" />
                Call (248) 478-8382
              </a>
              <a href="tel:8006824682" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2935">
                <Phone className="mr-3" size={24} data-id="element-2936" />
                MI Truck Safety: (800) 682-4682
              </a>
            </div>
            <p className="mt-4 text-gray-300" data-id="element-2937">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}